import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { SecurityService } from '../security/security.service';
import {
  HOME_DEMO_ENABLED,
  HomeSampleSource,
  HomeSeedContext,
  fillOrganization,
  fillPeopleStats,
  fillServices,
  noteHomeSample,
  rememberOrgType,
  rememberServiceExtras,
  rememberWrite,
  seedOffers,
  seedOrganizationInfo,
  seedOrganizationPart,
  seedPeoplePart,
  seedPermissions,
  seedWatching,
  writtenAt,
} from './home-demo-backend';
import { INVENTED_DATA_ENABLED } from './invented-data';

/**
 * Stands in for what the new Home asks and `backend/api` cannot answer yet. The rules and every seed
 * live in {@link ./home-demo-backend.ts}; this file is only the wiring.
 *
 * **To remove it:** delete the three `home-demo-backend*` files and the word
 * `homeDemoBackendInterceptor` from the `withInterceptors([...])` list in `app.config.ts`.
 */

/** The only page this may touch. Segment match: `/home_old` or `/homework` must never qualify. */
const HOME_ROUTE = '/home';

export function onHomeRoute(routerUrl: string): boolean {
  const path = routerUrl.split(/[?#]/)[0];
  return path === HOME_ROUTE || path.startsWith(`${HOME_ROUTE}/`);
}

/** What a request is, for this interceptor. `null` = not ours, pass straight through. */
export type HomeKind =
  // NEW endpoints: 404 → seed; writes answered 404 → remembered
  | { type: 'new-read'; source: HomeSampleSource; seed: (ctx: HomeSeedContext) => any; path: string; merge: 'object' | 'list' | 'none' }
  | { type: 'new-write'; source: HomeSampleSource; path: string; shape: 'object' | 'append' | 'replace' | 'sample' }
  // LIVE endpoints: fill absent keys / observe writes
  | { type: 'fill-organization'; orgId: number }
  | { type: 'fill-stats' }
  | { type: 'fill-services' }
  | { type: 'observe-org-type'; orgId: number }
  | { type: 'observe-service-write' }
  | { type: 'observe-public'; orgId: number };

/**
 * Sources whose stand-in is INVENTED content rather than an empty starting point: a plan, spend and
 * invoice figures, a WovenWord contact, an invitation nobody sent, watched quotes and offers. With
 * {@link INVENTED_DATA_ENABLED} off they keep their real 404 and the Home hides the card that would
 * have shown them.
 *
 * `permissions` is deliberately NOT here: its stand-in grants Finances from the reader's real admin
 * flag, nothing invented. Without it every admin would lose Billing, Payout and service rates on the
 * Home, since `/peoplePermissions` does not exist on the API yet.
 */
const INVENTED_SOURCES: ReadonlySet<HomeSampleSource> = new Set<HomeSampleSource>([
  'plan', 'spend', 'invoices', 'contact', 'invitation', 'watching', 'offers',
]);

const PART_SOURCE: Record<string, HomeSampleSource> = {
  work: 'org-work',
  usualLanguages: 'usual-languages',
  marketplace: 'marketplace',
  billing: 'billing',
  payout: 'payout',
  profile: 'org-summary',
};

const INFO_SOURCE: Record<string, HomeSampleSource> = {
  plan: 'plan',
  spend: 'spend',
  'invoices/summary': 'invoices',
  accountContact: 'contact',
};

const PEOPLE_SOURCE: Record<string, HomeSampleSource> = {
  invitation: 'invitation',
  languages: 'languages',
  availability: 'availability',
  workSamples: 'work-samples',
};

/** Classifies a request by method and path. Exported for the spec. */
export function classify(req: HttpRequest<unknown>): HomeKind | null {
  let path: string;
  try {
    path = new URL(req.url, 'http://x').pathname;
  } catch {
    path = req.url.split('?')[0];
  }
  const get = req.method === 'GET';
  let m: RegExpMatchArray | null;

  // ── NEW: organization parts, info
  if ((m = path.match(/\/organization\/(\d+)\/(work|usualLanguages|marketplace|billing|payout|profile)$/))) {
    const part = m[2];
    const key = `/organization/${m[1]}/${part}`;
    if (get && part !== 'profile') {
      return { type: 'new-read', source: PART_SOURCE[part], path: key, merge: 'object', seed: ctx => seedOrganizationPart(part, ctx) };
    }
    if (req.method === 'PUT') return { type: 'new-write', source: PART_SOURCE[part], path: key, shape: 'object' };
    return null;
  }
  if (get && (m = path.match(/\/organization\/(\d+)\/(plan|spend|invoices\/summary|accountContact)$/))) {
    const info = m[2];
    return { type: 'new-read', source: INFO_SOURCE[info], path: `/organization/${m[1]}/${info}`, merge: 'none', seed: ctx => seedOrganizationInfo(info, ctx) };
  }

  // ── NEW: person parts
  if ((m = path.match(/\/people\/(\d+)\/(invitation|languages|availability|workSamples)$/))) {
    const part = m[2];
    const key = `/people/${m[1]}/${part}`;
    if (get) {
      const merge = part === 'workSamples' ? 'list' : part === 'invitation' ? 'none' : 'object';
      return { type: 'new-read', source: PEOPLE_SOURCE[part], path: key, merge, seed: ctx => seedPeoplePart(part, ctx) };
    }
    if (req.method === 'PUT' && (part === 'languages' || part === 'availability')) {
      return { type: 'new-write', source: PEOPLE_SOURCE[part], path: key, shape: 'object' };
    }
    // One sample per linguist (Ansar, 29 Sep): today's POST and the contract's PUT both REPLACE it.
    if ((req.method === 'POST' || req.method === 'PUT') && part === 'workSamples') {
      return { type: 'new-write', source: 'work-samples', path: key, shape: 'sample' };
    }
    return null;
  }

  // ── NEW: permissions, capabilities, watching, offers
  if (get && /\/peoplePermissions$/.test(path)) {
    return { type: 'new-read', source: 'permissions', path: '/peoplePermissions', merge: 'none', seed: ctx => seedPermissions(idsFrom(req, 'peopleIds'), ctx) };
  }
  if (get && /\/peopleCapabilities$/.test(path)) {
    const ids = idsFrom(req, 'peopleIds');
    return {
      type: 'new-read', source: 'capabilities', path: '/peopleCapabilities', merge: 'none',
      // Only people with rows saved this session appear: "nothing recorded" is absence, not [].
      seed: () => Object.fromEntries(ids.filter(id => writtenAt(`/peopleCapabilities/${id}`)).map(id => [String(id), writtenAt(`/peopleCapabilities/${id}`)])),
    };
  }
  if (req.method === 'PUT' && (m = path.match(/\/peopleCapabilities\/(\d+)$/))) {
    return { type: 'new-write', source: 'capabilities', path: `/peopleCapabilities/${m[1]}`, shape: 'replace' };
  }
  if (get && (m = path.match(/\/peopleTasks\/(\d+)\/watching$/))) {
    return { type: 'new-read', source: 'watching', path: `/peopleTasks/${m[1]}/watching`, merge: 'none', seed: seedWatching };
  }
  if (get && (m = path.match(/\/peopleOffers\/(\d+)$/))) {
    return { type: 'new-read', source: 'offers', path: `/peopleOffers/${m[1]}`, merge: 'none', seed: seedOffers };
  }

  // ── LIVE: fills and observed writes
  if (get && (m = path.match(/\/organization\/(\d+)$/))) return { type: 'fill-organization', orgId: Number(m[1]) };
  if (get && /\/peopleStats\/\d+$/.test(path)) return { type: 'fill-stats' };
  if (get && /\/api\/v3\/services$/.test(path)) return { type: 'fill-services' };
  if (get && (m = path.match(/\/organizationTypes\/(\d+)$/))) return { type: 'observe-org-type', orgId: Number(m[1]) };
  if ((req.method === 'POST' || req.method === 'PUT') && /\/api\/v3\/services(\/[^/]+)?$/.test(path)) return { type: 'observe-service-write' };
  if (req.method === 'PUT' && (m = path.match(/\/api\/marketplace\/organizations\/(\d+)\/public$/))) return { type: 'observe-public', orgId: Number(m[1]) };

  return null;
}

function idsFrom(req: HttpRequest<unknown>, param: string): number[] {
  const raw = req.params.get(param) ?? new URL(req.url, 'http://x').searchParams.get(param) ?? '';
  return raw.split(',').map(p => Number(p.trim())).filter(id => Number.isFinite(id) && id > 0);
}

/**
 * 404 = no such endpoint on this server — the only status the sample layer answers, for reads and
 * writes alike. Status 0 (offline, CORS, server down) is a real failure and must reach the page:
 * answering it from memory would report a save as done once the endpoint exists and nothing was
 * stored. dev-api answers unknown paths with a real 404 carrying CORS headers, so 0 never means
 * "endpoint missing" there.
 */
export function missing(error: HttpErrorResponse): boolean {
  return error.status === 404;
}

function ok(req: HttpRequest<unknown>, body: any, source: HomeSampleSource): Observable<HttpEvent<unknown>> {
  noteHomeSample(source);
  return of(new HttpResponse({ status: 200, body, url: req.url }));
}

export const homeDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!HOME_DEMO_ENABLED) return next(req);
  const kind = classify(req);
  if (!kind) return next(req);

  // inject() only works here, in the injection context — not inside a pipe callback (NG0203).
  const router = inject(Router);
  const navigating = router.getCurrentNavigation()?.finalUrl?.toString();
  if (!onHomeRoute(navigating ?? router.url)) return next(req);

  const security = inject(SecurityService).securityObject;
  const ctx: HomeSeedContext = {
    orgId: Number(security?.orgId) || 0,
    peopleId: Number(security?.userId) || 0,
    isAdmin: !!security?.isAdmin,
    orgName: security?.orgName || '',
    now: new Date(),
  };

  switch (kind.type) {
    case 'new-read':
      return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (!missing(error)) return throwError(() => error);
          // Real data only: an invented answer is not given at all.
          if (!INVENTED_DATA_ENABLED && INVENTED_SOURCES.has(kind.source)) return throwError(() => error);
          const seed = kind.seed(ctx);
          const saved = writtenAt(kind.path);
          const body = kind.merge === 'object' ? { ...seed, ...(saved ?? {}) }
            : kind.merge === 'list' ? [...(Array.isArray(seed) ? seed : []), ...(Array.isArray(saved) ? saved : [])]
            : seed;
          // An empty starting point is not sample data; what the reader typed this session is (it is
          // kept in the browser only), so only that is labelled when nothing is invented.
          if (!INVENTED_DATA_ENABLED && saved === undefined) return of(new HttpResponse({ status: 200, body, url: req.url }));
          return ok(req, body, kind.source);
        }),
      );

    case 'new-write':
      return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (!missing(error)) return throwError(() => error);
          return ok(req, rememberNewWrite(kind, req, ctx), kind.source);
        }),
      );

    case 'fill-organization':
      return next(req).pipe(map(event => {
        if (!(event instanceof HttpResponse)) return event;
        const body = fillOrganization(event.body, kind.orgId);
        if (!body) return event;
        // The fill is empty unless the reader saved a summary or listing this session.
        const savedHere = writtenAt(`/organization/${kind.orgId}/profile`) || writtenAt(`/organization/${kind.orgId}/listing`);
        if (INVENTED_DATA_ENABLED || savedHere) noteHomeSample('org-summary');
        return event.clone({ body });
      }));

    case 'fill-stats':
      // "On time" assumes every delivery was; with real data only it stays unset.
      if (!INVENTED_DATA_ENABLED) return next(req);
      return next(req).pipe(map(event => {
        if (!(event instanceof HttpResponse)) return event;
        const body = fillPeopleStats(event.body, ctx.now);
        if (!body) return event;
        noteHomeSample('on-time');
        return event.clone({ body });
      }));

    case 'fill-services':
      return next(req).pipe(map(event => {
        if (!(event instanceof HttpResponse)) return event;
        const body = fillServices(event.body);
        if (!body) return event;
        noteHomeSample('service-details');
        return event.clone({ body });
      }));

    case 'observe-org-type':
      // Read only: the plan seed needs to know a freelancer's organization. The response is untouched.
      return next(req).pipe(tap(event => {
        if (event instanceof HttpResponse) rememberOrgType(kind.orgId, (event.body as any)?.orgType ?? null);
      }));

    case 'observe-service-write':
      // The live services API drops the service key, content types and experience today. The request
      // goes out unchanged; what it carried is remembered against the id the server returns.
      return next(req).pipe(tap(event => {
        if (!(event instanceof HttpResponse)) return;
        const id = String((event.body as any)?.id ?? req.url.match(/\/api\/v3\/services\/([^/?]+)/)?.[1] ?? '');
        rememberServiceExtras(id, req.body);
      }));

    case 'observe-public':
      // `isPublic` is not on GET /organization/{id} yet; remember what a successful write set.
      return next(req).pipe(tap(event => {
        if (event instanceof HttpResponse) {
          rememberWrite(`/organization/${kind.orgId}/listing`, { isPublic: req.params.get('value') === 'true' });
        }
      }));
  }
};

/** Keeps a write to a NEW endpoint and returns what the server would have answered. */
function rememberNewWrite(kind: Extract<HomeKind, { type: 'new-write' }>, req: HttpRequest<unknown>, ctx: HomeSeedContext): any {
  switch (kind.shape) {
    case 'object':
      return rememberWrite(kind.path, req.body);
    case 'replace':
      return rememberWrite(kind.path, req.body);
    case 'append': {
      const prev: any[] = writtenAt(kind.path) ?? [];
      const added = (Array.isArray(req.body) ? req.body : [req.body]).map((x: any, i: number) => ({
        ...x, id: `sample-invite-${prev.length + i + 1}`, status: 'pending', createdAt: ctx.now.toISOString(),
      }));
      rememberWrite(kind.path, [...prev, ...added]);
      return added;
    }
    case 'sample': {
      const form = req.body instanceof FormData ? req.body : null;
      const file = form?.get('file') as File | null;
      const row = {
        id: `sample-file-${Date.now()}`,
        name: file?.name ?? 'sample',
        size: file?.size ?? 0,
        serviceKey: String(form?.get('serviceKey') ?? ''),
        uploadedAt: ctx.now.toISOString(),
      };
      // The new sample replaces the old one: the read merges a one-item list.
      rememberWrite(kind.path, [row]);
      return row;
    }
  }
}
